import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { PAYMENT_TERMS, type Invoice } from '@accounting/shared';
import { useInvoices, useReceiveCustomerPayment } from '../../hooks/useInvoices';
import { useContacts, useUpdateContact } from '../../hooks/useContacts';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';
import { formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';
import { invoiceStatusTag } from './invoices.index';

export const Route = createFileRoute('/_auth/customer-center/$id')({
  component: CustomerDetailPage,
});

const num = (v: string | undefined) => {
  const n = parseFloat(v ?? '');
  return Number.isFinite(n) ? n : 0;
};

function CustomerDetailPage() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const { data: invoices = [], isLoading } = useInvoices(companyId, { customerId: id });
  const { data: customers = [] } = useContacts(companyId, { contactType: 'customer', includeInactive: true });
  const customer = customers.find((c) => c.id === id);
  const updateContact = useUpdateContact(companyId);
  const [receiving, setReceiving] = useState(false);

  const addressLines = customer
    ? [
        customer.addressLine1,
        customer.addressLine2,
        [customer.city, [customer.region, customer.postalCode].filter(Boolean).join(' ').trim()]
          .filter(Boolean)
          .join(', '),
      ].filter((l): l is string => !!l)
    : [];

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  const outstanding = invoices
    .filter((i) => i.status !== 'void')
    .reduce((s, i) => s + parseFloat(i.outstandingAmount), 0);
  const pastDue = invoices
    .filter((i) => i.isOverdue)
    .reduce((s, i) => s + parseFloat(i.outstandingAmount), 0);
  const amountPaid = invoices
    .filter((i) => i.status !== 'void')
    .reduce((s, i) => s + (parseFloat(i.total) - parseFloat(i.outstandingAmount)), 0);
  const openInvoices = invoices.filter((i) => i.status === 'open' || i.status === 'partial');

  return (
    <div>
      <Link to="/customer-center" style={{ fontSize: 13 }}>← Customer Center</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>{customer?.name ?? 'Customer'}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary"
            disabled={openInvoices.length === 0}
            onClick={() => setReceiving((v) => !v)}
            title={openInvoices.length === 0 ? 'No outstanding invoices' : undefined}
          >
            Receive payment
          </button>
          <Link to="/invoices/new" className="btn-primary" style={{ textDecoration: 'none' }}>+ New invoice</Link>
        </div>
      </div>

      {customer && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="form-grid" style={{ marginBottom: 0 }}>
            <span className="muted">Email</span>
            <span>{customer.email ?? '—'}</span>
            <span className="muted">Phone</span>
            <span>{customer.phone ?? '—'}</span>
            <span className="muted">Address</span>
            <span>{addressLines.length > 0 ? addressLines.join(' · ') : '—'}</span>
            <span className="muted">Website</span>
            <span>
              {customer.website ? (
                <a href={customer.website} target="_blank" rel="noreferrer">{customer.website}</a>
              ) : '—'}
            </span>
            <span className="muted">Payment terms</span>
            <span>
              <select
                value={customer.paymentTerms ?? ''}
                onChange={(e) => updateContact.mutate({ id, input: { paymentTerms: e.target.value } })}
                disabled={updateContact.isPending}
                style={{ maxWidth: 200 }}
                title="Default terms applied to new invoices for this customer"
              >
                <option value="">— none —</option>
                {customer.paymentTerms &&
                  !(PAYMENT_TERMS as readonly string[]).includes(customer.paymentTerms) && (
                    <option value={customer.paymentTerms}>{customer.paymentTerms}</option>
                  )}
                {PAYMENT_TERMS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>applied to new invoices</span>
            </span>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="card" style={{ padding: 16, minWidth: 180 }}>
          <div className="muted" style={{ fontSize: 12 }}>Outstanding</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{formatCurrency(outstanding.toFixed(2))}</div>
        </div>
        <div className="card" style={{ padding: 16, minWidth: 180 }}>
          <div className="muted" style={{ fontSize: 12 }}>Past due</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: pastDue > 0 ? 'var(--bad)' : undefined }}>
            {formatCurrency(pastDue.toFixed(2))}
          </div>
        </div>
        <div className="card" style={{ padding: 16, minWidth: 180 }}>
          <div className="muted" style={{ fontSize: 12 }}>Amount paid</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: amountPaid > 0 ? 'var(--ok)' : undefined }}>
            {formatCurrency(amountPaid.toFixed(2))}
          </div>
        </div>
      </div>

      {receiving && openInvoices.length > 0 && (
        <ReceivePaymentPanel
          companyId={companyId}
          customerId={id}
          openInvoices={openInvoices}
          onClose={() => setReceiving(false)}
        />
      )}

      {isLoading && <p className="muted">Loading…</p>}
      {!isLoading && invoices.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No invoices for this customer yet.</p>
        </div>
      )}

      {invoices.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Date</th>
              <th>Due</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ textAlign: 'right' }}>Balance</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} style={{ opacity: inv.status === 'void' ? 0.6 : 1 }}>
                <td>{inv.invoiceNumber}</td>
                <td>{formatAccountingDate(inv.invoiceDate)}</td>
                <td>{formatAccountingDate(inv.dueDate)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(inv.total)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(inv.outstandingAmount)}</td>
                <td>{invoiceStatusTag(inv)}</td>
                <td>
                  <Link to="/invoices/$id" params={{ id: inv.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ReceivePaymentPanel({
  companyId,
  customerId,
  openInvoices,
  onClose,
}: {
  companyId: string;
  customerId: string;
  openInvoices: Invoice[];
  onClose: () => void;
}) {
  const { data: accounts = [] } = useAccounts(companyId);
  const receive = useReceiveCustomerPayment(companyId);
  const cashAccounts = accounts.filter((a) => a.isActive && a.allowDirectPosting && a.accountType === 'asset');
  const sorted = [...openInvoices].sort((a, b) => a.invoiceDate.localeCompare(b.invoiceDate));

  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [depositAccountId, setDepositAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [amountReceived, setAmountReceived] = useState('');
  const [alloc, setAlloc] = useState<Record<string, string>>({});

  useEffect(() => {
    if (accounts.length === 0) return;
    const undeposited = accounts.find((a) => a.code === '1900');
    setDepositAccountId((v) => v || undeposited?.id || cashAccounts[0]?.id || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.length]);

  // Distribute the entered amount across invoices oldest-first, capped at each balance.
  function autoApply() {
    let remaining = num(amountReceived);
    const next: Record<string, string> = {};
    for (const inv of sorted) {
      if (remaining <= 0) {
        next[inv.id] = '';
        continue;
      }
      const owed = parseFloat(inv.outstandingAmount);
      const take = Math.min(owed, remaining);
      next[inv.id] = take.toFixed(2);
      remaining = Math.round((remaining - take) * 100) / 100;
    }
    setAlloc(next);
  }

  const total = sorted.reduce((s, i) => s + num(alloc[i.id]), 0);
  const overAllocated = sorted.some((i) => num(alloc[i.id]) > parseFloat(i.outstandingAmount) + 0.001);
  const valid = total > 0 && !!depositAccountId && !overAllocated;

  const errorMessage =
    receive.error instanceof ApiError
      ? (receive.error.body as { error?: string } | null)?.error ?? 'Payment failed'
      : receive.error?.message;

  function submit() {
    const allocations = sorted
      .filter((i) => num(alloc[i.id]) > 0)
      .map((i) => ({ invoiceId: i.id, amount: String(num(alloc[i.id])) }));
    receive.mutate(
      { customerId, paymentDate, depositAccountId, reference: reference.trim() || undefined, allocations },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 16, margin: 0 }}>Receive payment</h2>
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
        <label style={{ fontSize: 13 }}>Date <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} /></label>
        <label style={{ fontSize: 13 }}>
          Deposit into{' '}
          <span style={{ display: 'inline-block', verticalAlign: 'middle', minWidth: 260 }}>
            <AccountPicker
              accounts={accounts}
              value={depositAccountId}
              onChange={setDepositAccountId}
              predicate={(a) => a.isActive && a.allowDirectPosting && a.accountType === 'asset'}
            />
          </span>
        </label>
        <label style={{ fontSize: 13 }}>Reference <input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={100} placeholder="check #, etc." style={{ width: 130 }} /></label>
        <label style={{ fontSize: 13 }}>
          Amount received{' '}
          <input inputMode="decimal" value={amountReceived} onChange={(e) => setAmountReceived(e.target.value)} placeholder="0.00" style={{ width: 110, textAlign: 'right' }} />
        </label>
        <button className="btn-secondary" onClick={autoApply} disabled={num(amountReceived) <= 0}>Auto-apply oldest first</button>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Invoice</th>
            <th>Due</th>
            <th style={{ textAlign: 'right' }}>Balance</th>
            <th style={{ textAlign: 'right' }}>Payment</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((inv) => {
            const over = num(alloc[inv.id]) > parseFloat(inv.outstandingAmount) + 0.001;
            return (
              <tr key={inv.id}>
                <td>{inv.invoiceNumber}</td>
                <td>{formatAccountingDate(inv.dueDate)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(inv.outstandingAmount)}</td>
                <td style={{ textAlign: 'right' }}>
                  <input
                    inputMode="decimal"
                    value={alloc[inv.id] ?? ''}
                    onChange={(e) => setAlloc((p) => ({ ...p, [inv.id]: e.target.value }))}
                    placeholder="0.00"
                    style={{ width: 100, textAlign: 'right', borderColor: over ? 'var(--bad)' : undefined }}
                  />
                </td>
              </tr>
            );
          })}
          <tr style={{ fontWeight: 700, borderTop: '1px solid var(--border)' }}>
            <td colSpan={3} style={{ textAlign: 'right' }}>Total applied</td>
            <td style={{ textAlign: 'right' }}>{formatCurrency(total.toFixed(2))}</td>
          </tr>
        </tbody>
      </table>

      {overAllocated && <div className="field-error" style={{ marginTop: 8 }}>A payment exceeds an invoice's balance.</div>}
      {errorMessage && <div className="field-error" style={{ marginTop: 8 }}>{errorMessage}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn-primary" onClick={submit} disabled={!valid || receive.isPending}>
          {receive.isPending ? 'Posting…' : `Receive ${formatCurrency(total.toFixed(2))}`}
        </button>
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
